import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import { AuthArtwork } from './AuthArtwork';
import logo from '../images/XiltriX-logo-white.png';
import '../styles/auth.scss';

/**
 * Replaces Amplify's default footer so the two navigation links read as one
 * block, and so the Sign In / Create Account tab bar can be hidden: the tabs are
 * the only route to sign-up by default, and `toSignUp` gives us the same
 * destination from a link instead.
 */
function SignInFooter() {
  const { toForgotPassword, toSignUp } = useAuthenticator((context) => [
    context.toForgotPassword,
    context.toSignUp,
  ]);

  return (
    <div className="x5-auth__links">
      <button type="button" className="x5-auth__link" onClick={toForgotPassword}>
        Forgot password?
      </button>
      <p className="x5-auth__signup">
        Don&apos;t have an account?{' '}
        <button type="button" className="x5-auth__link" onClick={toSignUp}>
          Sign up
        </button>
      </p>
    </div>
  );
}

/**
 * Hiding the tab list removed the only way back from Create Account - the
 * "Sign In" tab is still rendered but not visible, so without this a user who
 * follows the sign-up link is stranded until they reload the page.
 */
function SignUpFooter() {
  const { toSignIn } = useAuthenticator((context) => [context.toSignIn]);

  return (
    <p className="x5-auth__signup x5-auth__links">
      Already have an account?{' '}
      <button type="button" className="x5-auth__link" onClick={toSignIn}>
        Sign in
      </button>
    </p>
  );
}

/**
 * The signed-out screen: brand panel on the left, artwork on the right.
 *
 * The Amplify Authenticator still does the work - SRP, the confirmation-code
 * and forgot-password flows, and the force-change-password challenge every
 * AdminCreateUser account hits on first sign-in. Only the chrome is ours, so
 * none of those flows had to be reimplemented to restyle the page.
 */
export function AuthLayout() {
  return (
    <div className="x5-auth">
      <section className="x5-auth__panel">
        <div className="x5-auth__form">
          <img className="x5-auth__logo" src={logo} alt="XiltriX" />
          <h1 className="x5-auth__title">Log in</h1>

          {/*
            Federated sign-in (Microsoft / SSO) and passwordless slot in here,
            above a divider. Deliberately absent rather than shown disabled:
            this page is reachable on the public internet, and a button that
            does nothing reads as a broken portal. Each needs infrastructure
            that does not exist yet - an OAuth-enabled app client and a custom
            auth domain for federation, an Essentials-tier pool or custom auth
            triggers for magic links.
          */}

          <Authenticator
            hideSignUp={false}
            components={{ SignIn: { Footer: SignInFooter }, SignUp: { Footer: SignUpFooter } }}
            // The pool requires email and recovers accounts through
            // verified_email, so sign-up must collect it. Without this the
            // Create Account form asks only for a username and password and
            // produces accounts with no recovery path.
            signUpAttributes={['email']}
            formFields={{
              signIn: {
                // "Username", not "Email": this pool aliases only
                // preferred_username, so an email address will not authenticate
                // and labelling the field Email would strand people who try.
                username: { label: 'Username', placeholder: 'Your username' },
                password: { label: 'Password', placeholder: 'Your password' },
              },
            }}
          />
        </div>
      </section>

      <section className="x5-auth__art">
        <AuthArtwork />
        <div className="x5-auth__art-copy">
          <h2>Monitoring you can trust</h2>
          <p>Equipment failure is inevitable. Asset loss is preventable.</p>
        </div>
      </section>
    </div>
  );
}
